import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Palette, Globe, Image as ImageIcon, Upload, ExternalLink, Check, X,
  Copy, Lock, RefreshCw, Sparkles, Eye, Wand2, Megaphone, DollarSign, Mail,
  Building2, Plus, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BRAND = {
  company_name: '',
  logo_url: '',
  favicon_url: '',
  primary_color: '#10B981',
  secondary_color: '#0F172A',
  accent_color: '#06B6D4',
  subdomain_slug: '',
  custom_domain: '',
  domain_status: null,
  login_heading: '',
  login_subheading: '',
  footer_text: '',
  email_sender_name: '',
  hide_powered_by: false,
  booking_url: '',
  // Operator-only landing customisation
  landing_hero_title: '',
  landing_hero_subtitle: '',
  landing_currency_symbol: '',
  landing_currency_code: '',
  landing_starter_price: '',
  landing_growth_price: '',
  landing_enterprise_price: '',
  landing_locale: '',
};

// Pretty status pill for the BYO-domain CNAME state
const DomainPill = ({ status }) => {
  if (!status) return null;
  const map = {
    pending_dns: { label: 'Pending DNS', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    verified:    { label: 'Verified ✓', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  };
  const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return <Badge variant="outline" className={m.cls} data-testid={`domain-status-${status}`}>{m.label}</Badge>;
};

const SectionHeader = ({ icon: Icon, title, hint }) => (
  <div className="flex items-start gap-3 mb-5">
    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
      <Icon className="h-5 w-5 text-emerald-600" />
    </div>
    <div>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {hint && <p className="text-sm text-gray-500 mt-0.5">{hint}</p>}
    </div>
  </div>
);

export default function MyWhitelabelPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [meta, setMeta] = useState({
    eligible: false, reason: '', role: '', subdomain_root: 'konnect.kaharian.com', dns_target: 'konnect.kaharian.com',
  });
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [slugCheck, setSlugCheck] = useState(null);
  const checkTimer = useRef(null);
  const fileRef = useRef(null);
  const faviconRef = useRef(null);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/whitelabel/me`);
      setMeta({
        eligible: !!r.data.eligible,
        reason: r.data.reason || '',
        role: (r.data.role || '').toLowerCase(),
        subdomain_root: r.data.subdomain_root || 'konnect.kaharian.com',
        dns_target: r.data.dns_target || 'konnect.kaharian.com',
      });
      setBrand({ ...DEFAULT_BRAND, ...(r.data.config || {}) });
    } catch (e) {
      console.error(e);
      toast.error('Failed to load whitelabel settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  const update = (patch) => setBrand((p) => ({ ...p, ...patch }));

  // Live availability check on subdomain typing
  useEffect(() => {
    if (!meta.eligible) return;
    const slug = (brand.subdomain_slug || '').toLowerCase();
    setSlugCheck(null);
    if (!slug) return;
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/whitelabel/check-subdomain`, { params: { slug } });
        setSlugCheck(r.data);
      } catch (e) {
        setSlugCheck({ available: false, reason: 'Could not check availability' });
      }
    }, 350);
    return () => clearTimeout(checkTimer.current);
  }, [brand.subdomain_slug, meta.eligible]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...brand };
      // strip server-managed fields
      delete payload.domain_status;
      delete payload.tenant_id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.owner_email;
      const r = await axios.put(`${API}/api/whitelabel/me`, payload);
      setBrand({ ...DEFAULT_BRAND, ...(r.data.config || {}) });
      toast.success('Whitelabel saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save whitelabel');
    } finally {
      setSaving(false);
    }
  };

  // Send a sample branded email to the user's own inbox so they can verify
  // the logo / color / footer render correctly across email clients BEFORE
  // sending it to a real customer.
  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      // Save first so the email uses the latest unsaved tweaks.
      await axios.put(`${API}/api/whitelabel/me`, {
        ...brand,
        domain_status: undefined,
        tenant_id: undefined,
        created_at: undefined,
        updated_at: undefined,
        owner_email: undefined,
      });
      const r = await axios.post(`${API}/api/whitelabel/test-email`);
      toast.success(`Brand-preview email sent to ${r.data.sent_to}. Check your inbox.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!brand.custom_domain) return;
    // The Verify endpoint reads the saved custom_domain from the DB. If the
    // user typed a domain but never clicked Save, the DB still has the old
    // (or empty) value and verify returns "No custom domain configured".
    // Auto-save first so verify always sees the latest input.
    setVerifying(true);
    try {
      await axios.put(`${API}/api/whitelabel/me`, { custom_domain: brand.custom_domain });
      const r = await axios.post(`${API}/api/whitelabel/me/verify-domain`);
      setBrand((p) => ({ ...p, domain_status: r.data.status }));
      if (r.data.status === 'verified') toast.success('Domain verified ✓');
      else toast.warning(r.data.detail || 'Pending DNS — check CNAME and try again.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const onLogoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) { toast.error('Logo too large (max 1.5 MB)'); return; }
    const r = new FileReader();
    r.onload = () => update({ logo_url: r.result });
    r.readAsDataURL(f);
  };

  const onFaviconFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 300_000) { toast.error('Favicon too large (max 300 KB)'); return; }
    const r = new FileReader();
    r.onload = () => update({ favicon_url: r.result });
    r.readAsDataURL(f);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  const previewUrl = brand.subdomain_slug
    ? `https://${brand.subdomain_slug}.${meta.subdomain_root}`
    : null;

  // ─── Brand DNA modal state ─────────────────────────────────────
  const [dnaOpen, setDnaOpen] = useState(false);
  const [dnaUrl, setDnaUrl] = useState('');
  const [dnaLoading, setDnaLoading] = useState(false);
  const [dnaResult, setDnaResult] = useState(null);

  const runBrandDna = async () => {
    if (!dnaUrl.trim()) return;
    setDnaLoading(true);
    setDnaResult(null);
    try {
      const r = await axios.post(`${API}/api/whitelabel/brand-dna/analyze`, { url: dnaUrl.trim() });
      setDnaResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not analyze that URL');
    } finally {
      setDnaLoading(false);
    }
  };

  const applyBrandDna = () => {
    if (!dnaResult) return;
    const patch = {};
    [
      'company_name', 'brand_voice', 'tone',
      'primary_color', 'secondary_color', 'accent_color',
      'login_heading', 'login_subheading', 'footer_text',
    ].forEach((k) => { if (dnaResult[k]) patch[k] = dnaResult[k]; });
    setBrand((p) => ({ ...p, ...patch }));
    setDnaOpen(false);
    toast.success('Brand DNA applied — review and save.');
  };

  // ─── Not eligible view ────────────────────────────────────────────
  if (!loading && !meta.eligible) {
    return (
      <div className="max-w-3xl mx-auto page-shell">
        <Card className="border-2 border-amber-200 bg-amber-50/40">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 mx-auto mb-4 flex items-center justify-center">
              <Lock className="h-6 w-6 text-amber-700" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Whitelabel is an Enterprise feature</h2>
            <p className="text-gray-600 mb-6">{meta.reason || 'Upgrade to Enterprise to unlock custom domains and full brand control.'}</p>
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700" data-testid="my-whitelabel-upgrade-btn">
              <a href="/settings?tab=billing">View plans</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Main form ────────────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto page-shell" data-testid="my-whitelabel-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Sparkles className="h-7 w-7 text-emerald-500" /> My Whitelabel<PageInfoTooltip pageKey="my-whitelabel" />
          </h1>
          <p className="text-gray-500 mt-1">Make Konnect feel like your product — domain, logo, colors, login.</p>
        </div>
        <div className="flex items-center gap-2">
          {previewUrl && (
            <Button variant="outline" asChild data-testid="my-whitelabel-preview-btn">
              <a href={previewUrl} target="_blank" rel="noreferrer">
                <Eye className="h-4 w-4 mr-2" /> Preview
              </a>
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleSendTestEmail}
            disabled={sendingTest}
            data-testid="my-whitelabel-test-email-btn"
            title="Email yourself a sample branded message to verify logo + color + footer render correctly"
          >
            <Mail className="h-4 w-4 mr-2" />
            {sendingTest ? 'Sending…' : 'Send test email'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="my-whitelabel-save-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="domain" className="space-y-6">
        <TabsList className="bg-white border" data-testid="my-whitelabel-tabs">
          <TabsTrigger value="domain"><Globe className="h-4 w-4 mr-2" />Domain</TabsTrigger>
          <TabsTrigger value="brand"><Palette className="h-4 w-4 mr-2" />Brand</TabsTrigger>
          <TabsTrigger value="login"><ImageIcon className="h-4 w-4 mr-2" />Login Page</TabsTrigger>
          {(meta.role === 'country_operator' || meta.role === 'super_admin') && (
            <TabsTrigger value="landing" data-testid="landing-tab-trigger">
              <Megaphone className="h-4 w-4 mr-2" />Landing Page
            </TabsTrigger>
          )}
          {(meta.role === 'country_operator' || meta.role === 'super_admin') && (
            <TabsTrigger value="agency" data-testid="agency-tab-trigger">
              <Building2 className="h-4 w-4 mr-2" />Agency Options
            </TabsTrigger>
          )}
        </TabsList>

        {/* ─── DOMAIN ───────────────────────────────────────────── */}
        <TabsContent value="domain" className="space-y-6">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-5 pb-4 text-sm text-blue-900">
              <div className="font-semibold mb-2 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                How signing in works for you &amp; your customers
              </div>
              <ul className="space-y-1.5 ml-6 list-disc text-blue-900/90">
                <li>
                  <strong>You</strong> — can sign in at either <code className="px-1 py-0.5 bg-white rounded text-xs">konnect.kaharian.com</code> (Konnect-branded admin)
                  or <code className="px-1 py-0.5 bg-white rounded text-xs">{brand.subdomain_slug ? `${brand.subdomain_slug}.konnect.kaharian.com` : 'your-subdomain'}</code> /
                  your custom domain. Same login, same data — only the chrome changes.
                </li>
                <li>
                  <strong>Your customers</strong> — sign in at your branded URL only. If they accidentally hit konnect.kaharian.com,
                  we redirect them to your domain automatically.
                </li>
                <li>
                  Use the <strong>&quot;View as my customer →&quot;</strong> item in your profile dropdown (top right) to
                  open your branded URL in a new tab and verify the customer experience.
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <SectionHeader
                icon={Globe}
                title="Konnect-hosted subdomain"
                hint="Free, instant. Choose a slug — it goes live the moment you save."
              />
            </CardHeader>
            <CardContent>
              <Label htmlFor="slug">Subdomain</Label>
              <div className="flex items-stretch mt-2 max-w-2xl">
                <Input
                  id="slug"
                  value={brand.subdomain_slug || ''}
                  onChange={(e) => update({ subdomain_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="acme"
                  className="rounded-r-none font-mono"
                  data-testid="subdomain-slug-input"
                />
                <div className="flex items-center px-4 bg-gray-50 border border-l-0 border-gray-200 rounded-r-md text-sm text-gray-500">
                  .{meta.subdomain_root}
                </div>
              </div>
              {brand.subdomain_slug && slugCheck && (
                <div className={`mt-2 text-sm flex items-center gap-2 ${slugCheck.available ? 'text-emerald-600' : 'text-red-600'}`}>
                  {slugCheck.available ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                  {slugCheck.available
                    ? <>Available — your space will be at <code className="font-mono">{slugCheck.preview_url}</code></>
                    : slugCheck.reason}
                </div>
              )}
              <p className="mt-3 text-xs text-gray-500">3–40 chars · letters, numbers, dashes only.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <SectionHeader
                icon={ExternalLink}
                title="Bring your own domain"
                hint="Use a domain you already own (app.acme.com). Add a CNAME, then verify."
              />
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label htmlFor="customd">Custom domain</Label>
                <div className="flex items-stretch mt-2 max-w-2xl gap-2">
                  <Input
                    id="customd"
                    value={brand.custom_domain || ''}
                    onChange={(e) => update({ custom_domain: e.target.value.toLowerCase() })}
                    placeholder="app.acme.com"
                    className="font-mono"
                    data-testid="custom-domain-input"
                  />
                  <DomainPill status={brand.domain_status} />
                </div>
              </div>

              <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-emerald-900 mb-2">DNS setup</div>
                <div className="text-sm text-emerald-900/80 mb-3">
                  In your DNS provider (GoDaddy / Cloudflare / Namecheap / etc.), add a <strong>CNAME</strong> record.
                  Add the <code className="text-xs bg-white/70 px-1 rounded">www</code> variant too — without it, automatic SSL provisioning will fail.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div className="bg-white rounded border border-emerald-200 p-2.5">
                    <div className="text-gray-500 mb-1">Type</div>
                    <div className="font-mono font-semibold">CNAME</div>
                  </div>
                  <div className="bg-white rounded border border-emerald-200 p-2.5">
                    <div className="text-gray-500 mb-1">Host / Name</div>
                    <div className="font-mono font-semibold flex items-center justify-between gap-2">
                      <span className="truncate">{brand.custom_domain || 'app'}</span>
                      {brand.custom_domain && (
                        <button onClick={() => copyToClipboard(brand.custom_domain)} className="text-emerald-700 hover:text-emerald-900">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="bg-white rounded border border-emerald-200 p-2.5">
                    <div className="text-gray-500 mb-1">Points to</div>
                    <div className="font-mono font-semibold flex items-center justify-between gap-2">
                      <span className="truncate">{meta.dns_target}</span>
                      <button onClick={() => copyToClipboard(meta.dns_target)} className="text-emerald-700 hover:text-emerald-900">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-emerald-900/70">DNS can take 5 minutes to a few hours to propagate.</div>
                  <Button
                    onClick={handleVerifyDomain}
                    disabled={!brand.custom_domain || verifying}
                    variant="outline"
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                    data-testid="verify-domain-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${verifying ? 'animate-spin' : ''}`} />
                    {verifying ? 'Verifying…' : 'Verify now'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── BRAND ────────────────────────────────────────────── */}
        <TabsContent value="brand" className="space-y-6">
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardContent className="p-4 flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Wand2 className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-900 text-sm">Brand DNA Engine</h3>
                  <p className="text-xs text-emerald-900/70 max-w-xl">
                    Paste your website URL and we'll auto-extract your name, colors, voice and tone — then drop them straight into this form.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setDnaOpen(true)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="brand-dna-open-btn"
              >
                <Wand2 className="h-4 w-4 mr-2" /> Analyze my website
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <SectionHeader icon={Palette} title="Identity" hint="Logo, colors, and footer copy applied across the dashboard." />
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="cn">Company name</Label>
                <Input
                  id="cn"
                  value={brand.company_name || ''}
                  onChange={(e) => update({ company_name: e.target.value })}
                  className="mt-2 max-w-xl"
                  placeholder="Acme Coffee Co."
                  data-testid="brand-company-name-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Logo</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                      {brand.logo_url
                        ? <img src={brand.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
                        : <ImageIcon className="h-8 w-8 text-gray-300" />}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogoFile} />
                      <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="brand-logo-upload-btn">
                        <Upload className="h-4 w-4 mr-2" /> Upload
                      </Button>
                      {brand.logo_url && (
                        <Button variant="ghost" size="sm" onClick={() => update({ logo_url: '' })} data-testid="brand-logo-clear-btn">
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">PNG/SVG with transparency works best · max 1.5 MB.</p>
                </div>

                <div>
                  <Label>Favicon <span className="text-gray-400 font-normal">(browser-tab icon)</span></Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                      {brand.favicon_url
                        ? <img src={brand.favicon_url} alt="favicon" className="max-w-full max-h-full object-contain" />
                        : brand.logo_url
                          ? <img src={brand.logo_url} alt="logo-as-favicon" className="max-w-full max-h-full object-contain opacity-40" />
                          : <ImageIcon className="h-6 w-6 text-gray-300" />}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input ref={faviconRef} type="file" accept="image/png,image/x-icon,image/svg+xml,image/jpeg" className="hidden" onChange={onFaviconFile} data-testid="brand-favicon-input" />
                      <Button variant="outline" size="sm" onClick={() => faviconRef.current?.click()} data-testid="brand-favicon-upload-btn">
                        <Upload className="h-4 w-4 mr-2" /> Upload favicon
                      </Button>
                      {brand.favicon_url && (
                        <Button variant="ghost" size="sm" onClick={() => update({ favicon_url: '' })} data-testid="brand-favicon-clear-btn">
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Square PNG or .ico, ideally 64×64 · max 300 KB. {!brand.favicon_url && brand.logo_url ? 'If empty, we use your logo.' : ''}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  {[
                    { k: 'primary_color', label: 'Primary' },
                    { k: 'secondary_color', label: 'Secondary' },
                  ].map(({ k, label }) => (
                    <div key={k}>
                      <Label>{label}</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          type="color"
                          value={brand[k] || '#000000'}
                          onChange={(e) => update({ [k]: e.target.value })}
                          className="w-10 h-10 rounded border cursor-pointer"
                          data-testid={`brand-color-${k}`}
                        />
                        <Input
                          value={brand[k] || ''}
                          onChange={(e) => update({ [k]: e.target.value })}
                          className="flex-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                  ))}
                  <div className="col-span-2">
                    <Label>Accent</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="color"
                        value={brand.accent_color || '#06B6D4'}
                        onChange={(e) => update({ accent_color: e.target.value })}
                        className="w-10 h-10 rounded border cursor-pointer"
                        data-testid="brand-color-accent"
                      />
                      <Input
                        value={brand.accent_color || ''}
                        onChange={(e) => update({ accent_color: e.target.value })}
                        className="flex-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="footer">Footer text</Label>
                <Input
                  id="footer"
                  value={brand.footer_text || ''}
                  onChange={(e) => update({ footer_text: e.target.value })}
                  placeholder="© 2026 Acme Coffee — All rights reserved"
                  className="mt-2 max-w-2xl"
                  data-testid="brand-footer-text"
                />
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg border bg-gray-50">
                <Switch
                  checked={!!brand.hide_powered_by}
                  onCheckedChange={(v) => update({ hide_powered_by: v })}
                  data-testid="brand-hide-powered-by"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Hide "Powered by Konnect"</div>
                  <div className="text-xs text-gray-500">Removes the Konnect badge from the dashboard footer.</div>
                </div>
              </div>

              <div>
                <Label htmlFor="sender">Email sender name</Label>
                <Input
                  id="sender"
                  value={brand.email_sender_name || ''}
                  onChange={(e) => update({ email_sender_name: e.target.value })}
                  placeholder="Acme Coffee"
                  className="mt-2 max-w-xl"
                  data-testid="brand-email-sender"
                />
                <p className="mt-1 text-xs text-gray-500">Shown as the "From" name on outgoing emails.</p>
              </div>

              <div>
                <Label htmlFor="booking-url">Demo booking URL <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="booking-url"
                  type="url"
                  value={brand.booking_url || ''}
                  onChange={(e) => update({ booking_url: e.target.value })}
                  placeholder="https://calendly.com/your-team/discovery-call"
                  className="mt-2 max-w-xl"
                  data-testid="brand-booking-url"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Paste your Calendly, Cal.com, Google Calendar, or HubSpot Meetings link. When set,
                  the final slide of your public Sales Deck shows a <strong>"Book directly on my calendar →"</strong> button instead
                  of the lead-capture form, sending prospects straight to a confirmed time slot.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── LOGIN PAGE ──────────────────────────────────────── */}
        <TabsContent value="login" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <SectionHeader icon={ImageIcon} title="Login page copy" hint="Customers see this on your subdomain or custom domain." />
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label htmlFor="lh">Heading</Label>
                  <Input
                    id="lh"
                    value={brand.login_heading || ''}
                    onChange={(e) => update({ login_heading: e.target.value })}
                    placeholder="Welcome to Acme"
                    className="mt-2"
                    data-testid="login-heading-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lsh">Sub-heading</Label>
                  <Textarea
                    id="lsh"
                    value={brand.login_subheading || ''}
                    onChange={(e) => update({ login_subheading: e.target.value })}
                    placeholder="Sign in to your operations cockpit"
                    className="mt-2"
                    rows={3}
                    data-testid="login-subheading-input"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Live preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="rounded-xl overflow-hidden border shadow-sm"
                  style={{ background: brand.secondary_color || '#0F172A' }}
                  data-testid="login-preview-card"
                >
                  <div className="px-8 py-10 text-center">
                    {brand.logo_url
                      ? <img src={brand.logo_url} alt="logo" className="h-12 mx-auto mb-5 object-contain" />
                      : <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-white/10 flex items-center justify-center text-white font-bold">
                          {(brand.company_name || 'K').slice(0, 1).toUpperCase()}
                        </div>}
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {brand.login_heading || `Welcome to ${brand.company_name || 'your workspace'}`}
                    </h2>
                    <p className="text-sm text-white/70 max-w-sm mx-auto">
                      {brand.login_subheading || 'Sign in to continue.'}
                    </p>
                    <div className="mt-6 max-w-xs mx-auto space-y-3">
                      <div className="h-10 rounded-md bg-white/95"></div>
                      <div className="h-10 rounded-md bg-white/95"></div>
                      <div
                        className="h-10 rounded-md flex items-center justify-center text-white font-medium text-sm"
                        style={{ background: brand.primary_color || '#10B981' }}
                      >
                        Sign in
                      </div>
                    </div>
                    {!brand.hide_powered_by && (
                      <div className="mt-6 text-[10px] text-white/40">
                        Powered by Konnect
                      </div>
                    )}
                    {brand.footer_text && (
                      <div className="mt-2 text-[10px] text-white/40">{brand.footer_text}</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ─── LANDING PAGE (operator-only) ────────────────────────── */}
        {(meta.role === 'country_operator' || meta.role === 'super_admin') && (
          <TabsContent value="landing" className="space-y-6">
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-4 text-sm text-emerald-900">
                <strong>Country/Area Operator landing page.</strong> When companies visit your subdomain root,
                they'll see this customised landing page (instead of the default Konnect marketing page) so
                they can sign up under your operation. Translate copy, set local currency, and adjust pricing
                for your region. Enterprise subscribers don't see this — their subdomain root goes straight to login.
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <SectionHeader icon={Megaphone} title="Hero & language" hint="Top-of-page headline that visitors see first." />
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <Label htmlFor="hero-title">Hero headline</Label>
                    <Input
                      id="hero-title"
                      value={brand.landing_hero_title || ''}
                      onChange={(e) => update({ landing_hero_title: e.target.value })}
                      placeholder="Built for Bahrain businesses"
                      className="mt-2"
                      data-testid="landing-hero-title-input"
                    />
                    <p className="mt-1 text-xs text-gray-500">Leave blank to keep the default Konnect headline.</p>
                  </div>
                  <div>
                    <Label htmlFor="hero-sub">Sub-headline</Label>
                    <Textarea
                      id="hero-sub"
                      value={brand.landing_hero_subtitle || ''}
                      onChange={(e) => update({ landing_hero_subtitle: e.target.value })}
                      placeholder="One platform for Bahrain SMEs to run sales, marketing, support, and operations — in Arabic & English, priced in BHD."
                      className="mt-2"
                      rows={3}
                      data-testid="landing-hero-subtitle-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="locale">Locale</Label>
                    <Input
                      id="locale"
                      value={brand.landing_locale || ''}
                      onChange={(e) => update({ landing_locale: e.target.value.toLowerCase().slice(0, 8) })}
                      placeholder="en-bh, ar-bh, en-ph, etc."
                      className="mt-2 font-mono"
                      data-testid="landing-locale-input"
                    />
                    <p className="mt-1 text-xs text-gray-500">For future translation hooks. Format: language-region (e.g. en-bh).</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <SectionHeader icon={DollarSign} title="Currency & pricing" hint="Override Konnect's default $-prices for your region." />
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="cur-sym">Currency symbol</Label>
                      <Input
                        id="cur-sym"
                        value={brand.landing_currency_symbol || ''}
                        onChange={(e) => update({ landing_currency_symbol: e.target.value.slice(0, 8) })}
                        placeholder="BD, ₱, €, AED"
                        className="mt-2 font-mono"
                        data-testid="landing-currency-symbol-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cur-code">Currency code</Label>
                      <Input
                        id="cur-code"
                        value={brand.landing_currency_code || ''}
                        onChange={(e) => update({ landing_currency_code: e.target.value.toUpperCase().slice(0, 8) })}
                        placeholder="BHD, PHP, EUR, AED"
                        className="mt-2 font-mono"
                        data-testid="landing-currency-code-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="p-st">Starter price</Label>
                      <Input
                        id="p-st"
                        value={brand.landing_starter_price || ''}
                        onChange={(e) => update({ landing_starter_price: e.target.value })}
                        placeholder="120"
                        className="mt-2 font-mono"
                        data-testid="landing-starter-price-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="p-gr">Growth price</Label>
                      <Input
                        id="p-gr"
                        value={brand.landing_growth_price || ''}
                        onChange={(e) => update({ landing_growth_price: e.target.value })}
                        placeholder="280"
                        className="mt-2 font-mono"
                        data-testid="landing-growth-price-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="p-en">Enterprise price</Label>
                      <Input
                        id="p-en"
                        value={brand.landing_enterprise_price || ''}
                        onChange={(e) => update({ landing_enterprise_price: e.target.value })}
                        placeholder="Custom"
                        className="mt-2 font-mono"
                        data-testid="landing-enterprise-price-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Leave any price blank to fall back to the default $-priced plan with your currency symbol applied.
                    For Enterprise, the most natural value is "Custom".
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* ─── AGENCY OPTIONS (Country Operator only) ─────────────────────── */}
        {(meta.role === 'country_operator' || meta.role === 'super_admin') && (
          <TabsContent value="agency" className="space-y-6">
            <COAgencySettingsSection />
          </TabsContent>
        )}
      </Tabs>

      {/* ─── Brand DNA modal ──────────────────────────────────── */}
      <Dialog open={dnaOpen} onOpenChange={(o) => { setDnaOpen(o); if (!o) { setDnaResult(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="brand-dna-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-emerald-600" /> Analyze my brand
            </DialogTitle>
          </DialogHeader>

          {!dnaResult && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We'll fetch your homepage, extract the brand DNA (name · colors · voice · tone), and pre-fill the whitelabel form. Nothing is saved until you confirm.
              </p>
              <div>
                <Label htmlFor="dna-url">Website URL</Label>
                <Input
                  id="dna-url"
                  placeholder="https://acmecoffee.com"
                  value={dnaUrl}
                  onChange={(e) => setDnaUrl(e.target.value)}
                  className="mt-2 font-mono"
                  data-testid="brand-dna-url-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDnaOpen(false)}>Cancel</Button>
                <Button
                  onClick={runBrandDna}
                  disabled={!dnaUrl.trim() || dnaLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="brand-dna-run-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${dnaLoading ? 'animate-spin' : ''}`} />
                  {dnaLoading ? 'Analyzing…' : 'Analyze'}
                </Button>
              </DialogFooter>
            </div>
          )}

          {dnaResult && (
            <div className="space-y-4" data-testid="brand-dna-result">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-base font-semibold">{dnaResult.company_name || 'Brand profile'}</div>
                  <div className="text-xs text-gray-500">{dnaResult.suggested_industry || dnaResult.sources?.url}</div>
                </div>
                <div className="flex items-center gap-2">
                  {dnaResult.primary_color && (
                    <div className="w-8 h-8 rounded border" style={{ background: dnaResult.primary_color }} title={dnaResult.primary_color} />
                  )}
                  {dnaResult.secondary_color && (
                    <div className="w-8 h-8 rounded border" style={{ background: dnaResult.secondary_color }} title={dnaResult.secondary_color} />
                  )}
                  {dnaResult.accent_color && (
                    <div className="w-8 h-8 rounded border" style={{ background: dnaResult.accent_color }} title={dnaResult.accent_color} />
                  )}
                </div>
              </div>
              {dnaResult.brand_voice && (
                <div className="text-sm bg-gray-50 border rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Brand voice</div>
                  {dnaResult.brand_voice}
                </div>
              )}
              {dnaResult.login_heading && (
                <div className="text-sm">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Login heading</div>
                  <div className="font-semibold">{dnaResult.login_heading}</div>
                  <div className="text-xs text-gray-600">{dnaResult.login_subheading}</div>
                </div>
              )}
              {Array.isArray(dnaResult.key_themes) && dnaResult.key_themes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {dnaResult.key_themes.map((t, i) => (
                    <Badge key={i} variant="outline" className="border-emerald-200 text-emerald-800">{t}</Badge>
                  ))}
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setDnaResult(null)}>Re-analyze</Button>
                <Button onClick={applyBrandDna} className="bg-emerald-600 hover:bg-emerald-700" data-testid="brand-dna-apply-btn">
                  <Check className="h-4 w-4 mr-2" /> Apply to form
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Country Operator Agency Settings ─────────────────────────────────────
function COAgencySettingsSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agencyVisible, setAgencyVisible] = useState(true);
  const [locks, setLocks] = useState([]);
  const [notes, setNotes] = useState('');
  const [newLock, setNewLock] = useState({ location: '', agency_id: '', agency_name: '' });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/country-operator/settings`);
        setAgencyVisible(data.agency_option_visible !== false);
        setLocks(data.locked_agencies || []);
        setNotes(data.notes || '');
      } catch (e) {
        toast.error('Could not load agency settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addLock = () => {
    if (!newLock.location.trim() || !newLock.agency_id.trim()) {
      toast.error('Location and Agency ID are required');
      return;
    }
    setLocks((prev) => [...prev, { ...newLock }]);
    setNewLock({ location: '', agency_id: '', agency_name: '' });
  };

  const removeLock = (idx) => setLocks((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/country-operator/settings`, {
        agency_option_visible: agencyVisible,
        locked_agencies: locks,
        notes,
      });
      toast.success('Agency settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-500 p-6">Loading agency settings…</div>;

  return (
    <div className="space-y-6" data-testid="co-agency-settings">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-emerald-600" /> Agency visibility for your country
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border">
            <div className="flex-1 pr-4">
              <div className="font-medium text-sm text-gray-900">Show &quot;Agency&quot; option to subscribers</div>
              <p className="text-xs text-gray-600 mt-1">
                When disabled, new subscribers in your region will not see the &quot;Agency&quot; tier during signup.
                They can still be assigned to an agency manually by you.
              </p>
            </div>
            <Switch
              checked={agencyVisible}
              onCheckedChange={setAgencyVisible}
              data-testid="co-agency-visible-switch"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-600" /> Lock locations to specific agencies
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-gray-600">
            When a subscriber signs up from a locked location, they&apos;ll be automatically assigned to the
            matching agency instead of choosing one themselves.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <Input
              placeholder="Location (city, region, postal)"
              value={newLock.location}
              onChange={(e) => setNewLock({ ...newLock, location: e.target.value })}
              className="md:col-span-4"
              data-testid="co-lock-location-input"
            />
            <Input
              placeholder="Agency ID"
              value={newLock.agency_id}
              onChange={(e) => setNewLock({ ...newLock, agency_id: e.target.value })}
              className="md:col-span-3"
              data-testid="co-lock-agency-id-input"
            />
            <Input
              placeholder="Agency name (optional)"
              value={newLock.agency_name}
              onChange={(e) => setNewLock({ ...newLock, agency_name: e.target.value })}
              className="md:col-span-3"
              data-testid="co-lock-agency-name-input"
            />
            <Button onClick={addLock} className="md:col-span-2 bg-emerald-600 hover:bg-emerald-700" data-testid="co-add-lock-btn">
              <Plus className="h-4 w-4 mr-1" /> Add lock
            </Button>
          </div>

          {locks.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No locations locked. Subscribers pick their own agency.</p>
          ) : (
            <div className="space-y-2">
              {locks.map((lock, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white border rounded-lg" data-testid={`co-lock-row-${idx}`}>
                  <div className="flex-1 grid grid-cols-3 gap-3 text-sm">
                    <div><span className="text-xs text-gray-400 block">Location</span><span className="font-medium">{lock.location}</span></div>
                    <div><span className="text-xs text-gray-400 block">Agency ID</span><span className="font-mono text-xs">{lock.agency_id}</span></div>
                    <div><span className="text-xs text-gray-400 block">Agency name</span><span>{lock.agency_name || '—'}</span></div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeLock(idx)} data-testid={`co-remove-lock-${idx}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Internal notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Manila locked to Agency-01 until Q3 2026 per commercial agreement…"
            rows={3}
            data-testid="co-agency-notes"
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="co-agency-save-btn">
          {saving ? 'Saving…' : 'Save agency settings'}
        </Button>
      </div>
    </div>
  );
}

